import * as signalR from '@microsoft/signalr';
import { API_URL } from '../config/api';

export interface TaskData {
  id: string;
  type: string;
  name: string;
  status: string;
  startTime: string;
  endTime?: string;
  isCompleted: boolean;
  isViewed: boolean;
  errorMessage?: string;
  data?: any;
  knowledgeDocumentId?: string;
}

export interface TaskResultData extends TaskData {
  viewedAt?: string;
  taskId: string;
  error?: string;
}

export interface TaskUpdate {
  id: string;
  changes: Record<string, any>;
  updateType: string;
  updatedAt: string;
}

export interface TasksSummary {
  totalTasks: number;
  pendingTasks: number;
  completedTasks: number;
  failedTasks: number;
  unviewedTasks: number;
  lastTaskDate?: string;
}

export interface TasksBatchUpdate {
  updates: TaskUpdate[];
  timestamp: string;
}

export class TasksSignalRService {
  private connection: signalR.HubConnection | null = null;
  private token: string | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Начальная задержка в мс

  // Event handlers
  private taskCreatedHandlers: ((task: TaskData) => void)[] = [];
  private taskStatusChangedHandlers: ((task: TaskData) => void)[] = [];
  private taskCompletedHandlers: ((task: TaskResultData) => void)[] = [];
  private taskErrorHandlers: ((task: TaskData) => void)[] = [];
  private taskCancelledHandlers: ((task: TaskData) => void)[] = [];
  private taskMarkedAsViewedHandlers: ((taskId: string) => void)[] = [];
  private allTasksMarkedAsViewedHandlers: ((count: number) => void)[] = [];
  private tasksListUpdatedHandlers: ((data: { tasks: TaskData[] }) => void)[] = [];
  private unviewedTasksUpdatedHandlers: ((tasks: TaskData[]) => void)[] = [];
  private taskUpdatedHandlers: ((update: TaskUpdate) => void)[] = [];
  private tasksUpdatedHandlers: ((batchUpdate: TasksBatchUpdate) => void)[] = [];
  private tasksSummaryUpdatedHandlers: ((summary: TasksSummary) => void)[] = [];
  private connectionStatusHandlers: ((status: 'connected' | 'disconnected' | 'reconnecting' | 'error') => void)[] = [];

  constructor(token: string) {
    this.token = token;
  }

  /**
   * Подключение к TasksHub
   */
  async connect(): Promise<void> {
    if (this.isConnected) {
      return;
    }

    try {
      // API_URL обычно выглядит как ".../api". Хаб доступен на корне, поэтому убираем только хвост "/api" (с опциональным "/")
      const apiRoot = API_URL.replace(/\/api\/?$/i, '');
      const hubUrl = `${apiRoot}/tasksHub`;

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => this.token || '',
          skipNegotiation: false,
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.setupEventHandlers();
      
      await this.connection.start();
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      this.notifyConnectionStatus('connected');

      // Присоединяемся к группе пользователя для получения уведомлений
      await this.joinUserGroup();

    } catch (error) {
      console.error('Connection failed', error);
      this.notifyConnectionStatus('error');
      throw error;
    }
  }

  /**
   * Отключение от TasksHub
   */
  async disconnect(): Promise<void> {
    if (!this.connection) {
      return;
    }

    try {
      await this.leaveUserGroup();
      await this.connection.stop();
    } catch (error) {
      console.error('Disconnect error', error);
    } finally {
      this.isConnected = false;
      this.connection = null;
      this.notifyConnectionStatus('disconnected');
    }
  }

  /**
   * Настройка обработчиков событий SignalR
   */
  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Обработчики событий задач
    this.connection.on('TaskCreated', (task: TaskData) => {
      this.taskCreatedHandlers.forEach(handler => handler(task));
    });

    this.connection.on('TaskStatusChanged', (task: TaskData) => {
      this.taskStatusChangedHandlers.forEach(handler => handler(task));
    });

    this.connection.on('TaskCompleted', (task: TaskResultData) => {
      this.taskCompletedHandlers.forEach(handler => handler(task));
    });

    this.connection.on('TaskError', (task: TaskData) => {
      this.taskErrorHandlers.forEach(handler => handler(task));
    });

    this.connection.on('TaskCancelled', (task: TaskData) => {
      this.taskCancelledHandlers.forEach(handler => handler(task));
    });

    this.connection.on('TaskMarkedAsViewed', (taskId: string) => {
      this.taskMarkedAsViewedHandlers.forEach(handler => handler(taskId));
    });

    this.connection.on('AllTasksMarkedAsViewed', (count: number) => {
      this.allTasksMarkedAsViewedHandlers.forEach(handler => handler(count));
    });

    this.connection.on('TasksListUpdated', (data: { tasks: TaskData[] }) => {
      this.tasksListUpdatedHandlers.forEach(handler => handler(data));
    });

    this.connection.on('UnviewedTasksUpdated', (tasks: TaskData[]) => {
      this.unviewedTasksUpdatedHandlers.forEach(handler => handler(tasks));
    });

    // Новые оптимизированные события
    this.connection.on('TaskUpdated', (update: TaskUpdate) => {
      this.taskUpdatedHandlers.forEach(handler => handler(update));
    });

    this.connection.on('TasksUpdated', (batchUpdate: TasksBatchUpdate) => {
      this.tasksUpdatedHandlers.forEach(handler => handler(batchUpdate));
    });

    this.connection.on('TasksSummaryUpdated', (summary: TasksSummary) => {
      this.tasksSummaryUpdatedHandlers.forEach(handler => handler(summary));
    });

    // Обработчики ошибок
    this.connection.on('TaskStatusError', (taskId: string, error: string) => {
      console.error('Task status error', taskId, error);
    });

    this.connection.on('TaskResultError', (taskId: string, error: string) => {
      console.error('Task result error', taskId, error);
    });

    this.connection.on('TaskCancelError', (taskId: string, error: string) => {
      console.error('Task cancel error', taskId, error);
    });

    // Обработчики соединения
    this.connection.onreconnecting(() => {
      this.isConnected = false;
      this.notifyConnectionStatus('reconnecting');
    });

    this.connection.onreconnected(() => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.notifyConnectionStatus('connected');
      // Повторно присоединяемся к группе пользователя
      this.joinUserGroup().catch(error => 
        console.error('Failed to rejoin user group after reconnect', error)
      );
    });

    this.connection.onclose((error) => {
      this.isConnected = false;
      this.notifyConnectionStatus('disconnected');
      
      // Пытаемся переподключиться, если это не было намеренное отключение
      if (error && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    });
  }

  /**
   * Планирование переподключения
   */
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Экспоненциальная задержка
    
    setTimeout(() => {
      if (!this.isConnected) {
        this.connect().catch(error => {
          console.error('Reconnect attempt failed', error);
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        });
      }
    }, delay);
  }

  /**
   * Присоединение к группе пользователя
   */
  private async joinUserGroup(): Promise<void> {
    if (!this.connection || !this.isConnected) {
      throw new Error('Connection not established');
    }

    try {
      await this.connection.invoke('JoinUserGroup');
    } catch (error) {
      console.error('Failed to join user group', error);
      throw error;
    }
  }

  /**
   * Выход из группы пользователя
   */
  private async leaveUserGroup(): Promise<void> {
    if (!this.connection || !this.isConnected) {
      return;
    }

    try {
      await this.connection.invoke('LeaveUserGroup');
    } catch (error) {
      console.error('Failed to leave user group', error);
    }
  }

  /**
   * Получение статуса конкретной задачи
   */
  async getTaskStatus(taskId: string): Promise<void> {
    if (!this.connection || !this.isConnected) {
      throw new Error('Connection not established');
    }

    try {
      await this.connection.invoke('GetTaskStatus', taskId);
    } catch (error) {
      console.error('Failed to get task status', error);
      throw error;
    }
  }

  /**
   * Получение результата задачи
   */
  async getTaskResult(taskId: string): Promise<void> {
    if (!this.connection || !this.isConnected) {
      throw new Error('Connection not established');
    }

    try {
      await this.connection.invoke('GetTaskResult', taskId);
    } catch (error) {
      console.error('Failed to get task result', error);
      throw error;
    }
  }

  /**
   * Отмена задачи
   */
  async cancelTask(taskId: string): Promise<void> {
    if (!this.connection || !this.isConnected) {
      throw new Error('Connection not established');
    }

    try {
      await this.connection.invoke('CancelTask', taskId);
    } catch (error) {
      console.error('Failed to cancel task', error);
      throw error;
    }
  }

  /**
   * Пометка задачи как просмотренной
   */
  async markTaskAsViewed(taskId: string): Promise<void> {
    if (!this.connection || !this.isConnected) {
      throw new Error('Connection not established');
    }

    try {
      await this.connection.invoke('MarkTaskAsViewed', taskId);
    } catch (error) {
      console.error('Failed to mark task as viewed', error);
      throw error;
    }
  }

  /**
   * Пометка всех задач как просмотренных
   */
  async markAllTasksAsViewed(): Promise<void> {
    if (!this.connection || !this.isConnected) {
      throw new Error('Connection not established');
    }

    try {
      await this.connection.invoke('MarkAllTasksAsViewed');
    } catch (error) {
      console.error('Failed to mark all tasks as viewed', error);
      throw error;
    }
  }

  /**
   * Получение непросмотренных задач
   */
  async getUnviewedTasks(): Promise<void> {
    if (!this.connection || !this.isConnected) {
      throw new Error('Connection not established');
    }

    try {
      await this.connection.invoke('GetUnviewedTasks');
    } catch (error) {
      console.error('Failed to get unviewed tasks', error);
      throw error;
    }
  }

  // Event subscription methods
  onTaskCreated(handler: (task: TaskData) => void): () => void {
    this.taskCreatedHandlers.push(handler);
    return () => {
      const index = this.taskCreatedHandlers.indexOf(handler);
      if (index > -1) {
        this.taskCreatedHandlers.splice(index, 1);
      }
    };
  }

  onTaskStatusChanged(handler: (task: TaskData) => void): () => void {
    this.taskStatusChangedHandlers.push(handler);
    return () => {
      const index = this.taskStatusChangedHandlers.indexOf(handler);
      if (index > -1) {
        this.taskStatusChangedHandlers.splice(index, 1);
      }
    };
  }

  onTaskCompleted(handler: (task: TaskResultData) => void): () => void {
    this.taskCompletedHandlers.push(handler);
    return () => {
      const index = this.taskCompletedHandlers.indexOf(handler);
      if (index > -1) {
        this.taskCompletedHandlers.splice(index, 1);
      }
    };
  }

  onTaskError(handler: (task: TaskData) => void): () => void {
    this.taskErrorHandlers.push(handler);
    return () => {
      const index = this.taskErrorHandlers.indexOf(handler);
      if (index > -1) {
        this.taskErrorHandlers.splice(index, 1);
      }
    };
  }

  onTaskCancelled(handler: (task: TaskData) => void): () => void {
    this.taskCancelledHandlers.push(handler);
    return () => {
      const index = this.taskCancelledHandlers.indexOf(handler);
      if (index > -1) {
        this.taskCancelledHandlers.splice(index, 1);
      }
    };
  }

  onTaskMarkedAsViewed(handler: (taskId: string) => void): () => void {
    this.taskMarkedAsViewedHandlers.push(handler);
    return () => {
      const index = this.taskMarkedAsViewedHandlers.indexOf(handler);
      if (index > -1) {
        this.taskMarkedAsViewedHandlers.splice(index, 1);
      }
    };
  }

  onAllTasksMarkedAsViewed(handler: (count: number) => void): () => void {
    this.allTasksMarkedAsViewedHandlers.push(handler);
    return () => {
      const index = this.allTasksMarkedAsViewedHandlers.indexOf(handler);
      if (index > -1) {
        this.allTasksMarkedAsViewedHandlers.splice(index, 1);
      }
    };
  }

  onTasksListUpdated(handler: (data: { tasks: TaskData[] }) => void): () => void {
    this.tasksListUpdatedHandlers.push(handler);
    return () => {
      const index = this.tasksListUpdatedHandlers.indexOf(handler);
      if (index > -1) {
        this.tasksListUpdatedHandlers.splice(index, 1);
      }
    };
  }

  onUnviewedTasksUpdated(handler: (tasks: TaskData[]) => void): () => void {
    this.unviewedTasksUpdatedHandlers.push(handler);
    return () => {
      const index = this.unviewedTasksUpdatedHandlers.indexOf(handler);
      if (index > -1) {
        this.unviewedTasksUpdatedHandlers.splice(index, 1);
      }
    };
  }

  onConnectionStatusChanged(handler: (status: 'connected' | 'disconnected' | 'reconnecting' | 'error') => void): () => void {
    this.connectionStatusHandlers.push(handler);
    return () => {
      const index = this.connectionStatusHandlers.indexOf(handler);
      if (index > -1) {
        this.connectionStatusHandlers.splice(index, 1);
      }
    };
  }

  // Новые методы подписки на оптимизированные события
  onTaskUpdated(handler: (update: TaskUpdate) => void): () => void {
    this.taskUpdatedHandlers.push(handler);
    return () => {
      const index = this.taskUpdatedHandlers.indexOf(handler);
      if (index > -1) {
        this.taskUpdatedHandlers.splice(index, 1);
      }
    };
  }

  onTasksUpdated(handler: (batchUpdate: TasksBatchUpdate) => void): () => void {
    this.tasksUpdatedHandlers.push(handler);
    return () => {
      const index = this.tasksUpdatedHandlers.indexOf(handler);
      if (index > -1) {
        this.tasksUpdatedHandlers.splice(index, 1);
      }
    };
  }

  onTasksSummaryUpdated(handler: (summary: TasksSummary) => void): () => void {
    this.tasksSummaryUpdatedHandlers.push(handler);
    return () => {
      const index = this.tasksSummaryUpdatedHandlers.indexOf(handler);
      if (index > -1) {
        this.tasksSummaryUpdatedHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Уведомление о изменении статуса соединения
   */
  private notifyConnectionStatus(status: 'connected' | 'disconnected' | 'reconnecting' | 'error'): void {
    this.connectionStatusHandlers.forEach(handler => handler(status));
  }

  /**
   * Проверка состояния соединения
   */
  get connectionState(): 'connected' | 'disconnected' | 'reconnecting' {
    if (!this.connection) return 'disconnected';
    
    switch (this.connection.state) {
      case signalR.HubConnectionState.Connected:
        return 'connected';
      case signalR.HubConnectionState.Reconnecting:
        return 'reconnecting';
      default:
        return 'disconnected';
    }
  }

  /**
   * Проверка подключения
   */
  get isConnectionEstablished(): boolean {
    return this.isConnected && this.connection?.state === signalR.HubConnectionState.Connected;
  }
} 